import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { inactive } from '../../shared/constants/constants';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { HeaderService } from '../../core/header/header.service';
import { LocalStorageService } from '../../core/local-storage/local-storage.service';
import { SessionStorageService } from '../../core/session-storage/session-storage.service';
import { UiHelperService } from '../../core/ui-helper/ui-helper.service';
import { UserRole } from '../../shared/enum/user-role.enum';
import { RoutePath } from '../../shared/models/route-path.enum';
import { ProfileImageLoad } from '../../store/user-profile/user-profile.actions';
import { isAnonymousUserKey, shopInfoKey } from './../../shared/constants/storage-key-constants';
import { SharedConfig } from './../../shared/models/shared-config.model';
import { Shop } from './../../shared/models/shop.model';
import { sharedConfig } from './../../shared/shared.config';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  loggedInUserFirstName = '';
  loggedInUserfullName = '';
  languageList = [];
  selectedLanguage: string;
  subscriptions = new Subscription();
  selectedCartEntriesQty = 0;
  isShopOwnerRole$ = new BehaviorSubject<boolean>(false);
  cartItemCount$ = new BehaviorSubject<number>(0);
  isUserLoggedIn$ = new BehaviorSubject<boolean>(false); // denotes both shopowner/customer logged in
  applyVDClass = false;
  showCartIcon = false;
  showHomeIcon = false;
  allowHeaderNavigation = false;
  hideLoginIcon$ = new BehaviorSubject<boolean>(false);
  hideUserInfo$ = new BehaviorSubject<boolean>(false);

  constructor(
    private uiHelperService: UiHelperService,
    private authService: AuthService,
    private router: Router,
    private sessionStorage: SessionStorageService,
    private localStorage: LocalStorageService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private headerService: HeaderService,
    @Inject(sharedConfig) public config: SharedConfig,
  ) {
    this.subscribeForUserInfo();
  }

  /**
   * Function triggered on component initialization to setup the language configurations
   *
   * @memberof HeaderComponent
   */
  ngOnInit(): void {
    this.languageList = this.config.availableLanguages;
    this.selectedLanguage = this.uiHelperService.selectedLanguage;
    this.cartItemCount$ = this.headerService.cartItemCount$;
    this.isUserLoggedIn$ = this.headerService.isUserLoggedIn$;
    this.subscribeForVDClass();
    this.subscribeForCartIcon();
    this.subscribeForHomeIcon();
    this.subscribeForHeaderNavigation();
    this.subscribeForLoginIcon();
    this.subscribeForHideUserInfo();
  }

  /**
   * @description to subscribe for vd class subject
   * @memberof HeaderComponent
   */
  subscribeForVDClass() {
    this.subscriptions.add(
      this.headerService.applyVDClass$.subscribe(
        (val) => {
          if (val !== undefined && val !== null) {
            this.applyVDClass = val;
            this.cd.detectChanges();
          }
        }
      )
    );
  }

  /**
   * @description to subscribe for show cart icon subject
   * @memberof HeaderComponent
   */
  subscribeForCartIcon() {
    this.subscriptions.add(
      this.headerService.showCartIcon$.subscribe(
        (val) => {
          if (val !== undefined && val !== null) {
            this.showCartIcon = val;
            this.cd.detectChanges();
          }
        }
      )
    );
  }

  subscribeForHomeIcon() {
    this.subscriptions.add(
      this.headerService.showHomeIcon$.subscribe(
        (val) => {
          if (val !== undefined && val !== null) {
            this.showHomeIcon = val;
            this.cd.detectChanges();
          }
        }
      )
    );
  }

  /**
   * @name subscribeForLoginIcon
   * @description this method subscribe hide login icon
   * @memberof HeaderComponent
   */
  subscribeForLoginIcon() {
    this.subscriptions.add(
      this.headerService.hideLoginIcon$.subscribe(val => {
        const isAnonymoususer = this.sessionStorage.getItem(isAnonymousUserKey);
        const hide = val || isAnonymoususer ? true : false;
        this.hideLoginIcon$.next(hide);
      }));
  }

  /**
   * @name subscribeForLoginIcon
   * @description this method subscribe hide login icon
   * @memberof HeaderComponent
   */
  subscribeForHideUserInfo() {
    this.subscriptions.add(
      this.headerService.hideUserInfo$.subscribe(val => {
        const isAnonymoususer = this.sessionStorage.getItem(isAnonymousUserKey);
        const hide = val || isAnonymoususer ? true : false;
        this.hideUserInfo$.next(hide);
      }));
  }

  /**
   * @description to subscribe for allow header navigation subject
   * @memberof HeaderComponent
   */
  subscribeForHeaderNavigation() {
    this.subscriptions.add(
      this.headerService.allowHeaderNavigation$.subscribe(
        (val) => {
          if (val !== undefined && val !== null) {
            this.allowHeaderNavigation = val;
            this.cd.detectChanges();
          }
        }
      )
    );
  }

  /**
   * Function to subscribe for updated user info
   *
   * @memberof HeaderComponent
   */
  subscribeForUserInfo() {
    this.subscriptions.add(this.uiHelperService.roleChange$.subscribe(loaded => {
      if (loaded) {
        this.loadProfileImage();
      }
    }));
  }

  loadProfileImage() {
    const token = this.authService.getActiveJwtToken();
    if (token && token.accountStatus !== inactive) {
      const isShopOwner = this.authService.isRoleAvailable(UserRole.SHOP_OWNER) ? true : false;
      this.isShopOwnerRole$.next(isShopOwner);
      this.headerService.isUserLoggedIn$.next(true);
      this.loggedInUserFirstName = token.givenName;
      this.loggedInUserfullName = token.name;
      this.headerService.loggedInUserFullName$.next(this.loggedInUserfullName);
      this.store.dispatch(new ProfileImageLoad({ aboNum: token.aboid, partyId: token.partyId }));
    } else {
      this.isShopOwnerRole$.next(false);
      this.headerService.isUserLoggedIn$.next(false);
    }
  }

  /**
   * Callback function to route user to shop landing page
   *
   * @memberof HeaderComponent
   */
  viewLandingPage() {
    if (this.headerService.clearSessionBeforeNavigation$.getValue()) {
      const shopInfo = <Shop>this.sessionStorage.getItem(shopInfoKey);
      if (shopInfo && shopInfo.shopUid) {
        this.sessionStorage.clear();
        this.localStorage.clear();
        this.router.navigateByUrl(`${shopInfo.shopUid}`);
      }
    } else {
      this.uiHelperService.navigateToLandingPage();
    }
  }

  /**
   * Callback function to route user to shop landing page
   *
   * @memberof HeaderComponent
   */
  gotoSF() {
    window.location.href = environment.hybrisSfBasepath + '?checkSession=true';
  }

  /**
   * Functon to route user to cart page
   *
   * @memberof HeaderComponent
   */
  viewCartPage() {
    if (this.uiHelperService.doesCartExist()) {
      const shopInfo = <Shop>this.sessionStorage.getItem(shopInfoKey);
      if (shopInfo && shopInfo.shopUid) {
        this.router.navigateByUrl(`${shopInfo.shopUid + this.uiHelperService.getRoutePathForCartPage()}`);
      }
    } else {
      // do nothing
    }
  }

  /**
   * @name loginUser
   * @description this method redirects the user to idp for login
   * @memberof HeaderComponent
   */
  loginUser() {
    const shopInfo = <Shop>this.sessionStorage.getItem(shopInfoKey);
    if (shopInfo && shopInfo.shopUid) {
      if (this.router.url.endsWith(RoutePath.CART)) {
        this.router.navigateByUrl(`${shopInfo.shopUid + RoutePath.AUTH_OCC}`);
      } else {
        this.router.navigateByUrl(`${shopInfo.shopUid + RoutePath.AUTH_OCC + '?isFrom=' + this.router.url}`);
      }
    }
  }

  /**
   * Function to unsubscribe
   *
   * @memberof HeaderComponent
   */
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
